import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { getCurrentUser, signOut } from 'aws-amplify/auth';

/**
 * AppLayout — auth guard + application shell.
 *
 * Checks Amplify session on mount. If unauthenticated, redirects to /login.
 * Wraps all authenticated routes via React Router's nested layout pattern.
 */
export default function AppLayout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState<string | null>(null);

  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        setUserName(user.username);
        setLoading(false);
      })
      .catch(() => {
        navigate('/login', { replace: true });
      });
  }, [navigate]);

  async function handleSignOut() {
    await signOut();
    navigate('/login', { replace: true });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="inline-block w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-gray-200 flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100">
          <Link to="/" className="text-base font-bold text-gray-900 tracking-tight">
            Slingshotio
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavItem to="/" label="Dashboard" icon={<HomeIcon />} />
          <NavItem to="/resume" label="Resume" icon={<DocumentIcon />} />
          <NavItem to="/tools" label="AI Tools" icon={<SparkleIcon />} />
          <NavItem to="/folio" label="Folio" icon={<GlobeIcon />} />
          <NavItem to="/billing" label="Billing" icon={<CreditCardIcon />} />
          <NavItem to="/settings" label="Settings" icon={<CogIcon />} />
        </nav>

        <div className="px-3 py-4 border-t border-gray-100">
          <div className="text-xs text-gray-500 truncate px-2 mb-2">{userName}</div>
          <button
            onClick={handleSignOut}
            className="w-full text-left px-2 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({ to, label, icon }: { to: string; label: string; icon: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-2 py-1.5 text-sm rounded-md transition-colors ${
          isActive
            ? 'bg-indigo-50 text-indigo-700 font-medium'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        }`
      }
    >
      <span className="w-4 h-4 flex-shrink-0">{icon}</span>
      {label}
    </NavLink>
  );
}

function HomeIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1L1 7v8h5v-4h4v4h5V7L8 1z" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="1" width="10" height="14" rx="1.5" />
      <path d="M6 5h4M6 8h4M6 11h2" strokeLinecap="round" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1l1.5 4H14l-3.5 2.5 1.5 4L8 9l-4 2.5 1.5-4L2 5h4.5L8 1z" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 1.5C8 1.5 5.5 4 5.5 8s2.5 6.5 2.5 6.5S10.5 12 10.5 8 8 1.5 8 1.5z" />
      <path d="M1.5 8h13" strokeLinecap="round" />
    </svg>
  );
}

function CreditCardIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="3.5" width="14" height="9" rx="1.5" />
      <path d="M1 6.5h14" strokeLinecap="round" />
    </svg>
  );
}

function CogIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M8 5.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5zM4 8a4 4 0 118 0 4 4 0 01-8 0z"
        clipRule="evenodd"
      />
      <path
        fillRule="evenodd"
        d="M7.12.12a1 1 0 011.76 0l.52.9.9-.52a1 1 0 011.4 1.4l-.52.9.9.52a1 1 0 010 1.76l-.9.52.52.9a1 1 0 01-1.4 1.4l-.9-.52-.52.9a1 1 0 01-1.76 0l-.52-.9-.9.52a1 1 0 01-1.4-1.4l.52-.9-.9-.52a1 1 0 010-1.76l.9-.52-.52-.9a1 1 0 011.4-1.4l.9.52.52-.9z"
        clipRule="evenodd"
      />
    </svg>
  );
}
